<div class="asset-history-container">
  <!-- Header Bar -->
  <div class="history-header d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
    <div class="header-left d-flex align-items-center flex-wrap gap-2">
      <h6 class="m-0 font-weight-bold history-title d-flex align-items-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2">
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        Activity History
        @if (totalCounts > 0) {
          <span class="count-badge ml-2">{{ totalCounts }}</span>
        }
      </h6>

      <!-- Type Filter Pills (shown in unified mode) -->
      @if (isUnified) {
        <div class="type-filter-group btn-group btn-group-sm ml-2">
          <button
            type="button"
            class="btn filter-pill-btn"
            [class.active]="selectedTypeFilter === 'All'"
            (click)="setTypeFilter('All')">
            All
          </button>
          <button
            type="button"
            class="btn filter-pill-btn"
            [class.active]="selectedTypeFilter === 'Inventory'"
            (click)="setTypeFilter('Inventory')">
            Inventory
          </button>
          <button
            type="button"
            class="btn filter-pill-btn"
            [class.active]="selectedTypeFilter === 'Workstation'"
            (click)="setTypeFilter('Workstation')">
            Workstation
          </button>
        </div>
      } @else if (assetName || assetCode) {
        <div class="d-flex align-items-center ml-2">
          <span class="asset-context-badge">
            {{ assetName }}
            @if (assetCode) {
              <span class="ml-1 opacity-75">#{{ assetCode }}</span>
            }
          </span>
        </div>
      }
    </div>

    <div class="header-right">
      <button class="btn btn-sm btn-refresh" (click)="fetchLogs()" [disabled]="isLoading" title="Refresh history">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" [class.spin]="isLoading">
          <path d="M23 4v6h-6"></path>
          <path d="M1 20v-6h6"></path>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
        </svg>
      </button>
    </div>
  </div>

  <!-- Error Alert -->
  @if (errorMessage) {
    <div class="alert alert-danger history-alert d-flex align-items-center" role="alert">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-2 flex-shrink-0">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <div>{{ errorMessage }}</div>
    </div>
  }

  <!-- Loading State -->
  @if (isLoading) {
    <div class="timeline-loading text-center py-4">
      <div class="spinner-border text-primary spinner-sm" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="text-muted mt-2 mb-0 small">Fetching activity history...</p>
    </div>
  }

  <!-- Empty State -->
  @if (!isLoading && !errorMessage && logs.length === 0) {
    <div class="empty-history text-center py-4">
      <div class="empty-icon-box mb-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </div>
      <h6 class="font-weight-bold mb-1">No Activity Logs Found</h6>
      <p class="text-muted small mb-0">No actions recorded for the selected filter.</p>
    </div>
  }

  <!-- Timeline List -->
  @if (!isLoading && logs.length > 0) {
    <div class="history-timeline">
      @for (log of logs; track log._id) {
        <div class="timeline-item">
          <!-- Dot & Line -->
          <div class="timeline-marker">
            <span class="marker-dot" [ngClass]="getActionBadgeClass(log.action)"></span>
            <span class="marker-line"></span>
          </div>

          <!-- Content Card -->
          <div class="timeline-card">
            <!-- Top Row: Asset Info, Actor, Action Badge, Time in IST -->
            <div class="card-header-row d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="actor-and-asset d-flex align-items-center flex-wrap gap-2">
                <!-- Asset Type Pill -->
                <span class="asset-type-tag" [ngClass]="log.assetType?.toLowerCase()">
                  {{ log.assetType === 'Inventory' ? 'INV' : 'WS' }}
                </span>

                <!-- Asset Name & Code -->
                <span class="asset-item-title font-weight-bold" title="{{ log.assetName }}">
                  {{ log.assetName }}
                </span>
                @if (log.assetCode) {
                  <span class="code-pill">#{{ log.assetCode }}</span>
                }

                <!-- Actor Info -->
                <div class="actor-info-sub d-flex align-items-center ml-2">
                  @if (log.actorProfileImg) {
                    <img [src]="log.actorProfileImg" class="actor-avatar mr-1" alt="{{ log.actorName }}" />
                  } @else {
                    <div class="actor-avatar-placeholder mr-1">
                      {{ getInitials(log.actorName) }}
                    </div>
                  }
                  <span class="actor-name small text-muted">{{ log.actorName || 'System' }}</span>
                </div>

                <!-- Target User if present -->
                @if (log.targetUserName) {
                  <span class="target-user-pill ml-1" title="Target User">
                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <polyline points="17 11 19 13 23 9"></polyline>
                    </svg>
                    {{ log.targetUserName }}
                  </span>
                }
              </div>

              <div class="time-and-badge d-flex align-items-center mt-1 mt-sm-0">
                <span class="action-badge mr-2" [ngClass]="getActionBadgeClass(log.action)">
                  {{ log.action }}
                </span>
                <span class="time-ist text-muted small" title="Indian Standard Time">
                  {{ log.createdAt | date:'d MMM y, h:mm a':'+0530' }} IST
                </span>
              </div>
            </div>

            <!-- Summary Line -->
            <div class="card-summary mt-2">
              <p class="summary-text mb-0">{{ assetLogsService.buildMetaSummary(log) }}</p>
            </div>

            <!-- Structured Meta Details -->
            <div class="meta-details mt-2">
              <!-- 1. Update: field from -> to list -->
              @if (log.action === 'Update' && getUpdatedFields(log).length > 0) {
                <div class="diff-table-wrap">
                  <table class="table table-sm diff-table mb-0">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>From</th>
                        <th>To</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (item of getUpdatedFields(log); track item.field) {
                        <tr>
                          <td class="font-weight-bold field-col">{{ item.field }}</td>
                          <td class="diff-from"><span class="from-val">{{ formatValue(item.from) }}</span></td>
                          <td class="diff-to"><span class="to-val">{{ formatValue(item.to) }}</span></td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }

              <!-- 2. Image Actions: thumbnails -->
              @if (getImages(log).length > 0) {
                <div class="images-preview-wrap mt-2">
                  <div class="image-grid d-flex flex-wrap gap-2">
                    @for (img of getImages(log); track img) {
                      <div class="img-thumbnail-item" (click)="openImagePreview(img)" title="Click to enlarge">
                        <img [src]="img" alt="Asset Image" class="thumb-img" />
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- 3. Extend: old date -> new date -->
              @if (log.action === 'Extend' && (log.meta?.['from'] || log.meta?.['to'])) {
                <div class="extend-dates-box mt-2 d-flex align-items-center">
                  <span class="date-badge old-date">{{ (log.meta?.['from'] | date:'d MMM y') || 'N/A' }}</span>
                  <span class="arrow-sep mx-2">→</span>
                  <span class="date-badge new-date">{{ (log.meta?.['to'] | date:'d MMM y') || 'N/A' }}</span>
                </div>
              }

              <!-- 4. Request Details (Rejection reason, On-hold date, forward info) -->
              @if (log.meta?.['rejectionReason']) {
                <div class="meta-alert alert-rejection mt-2">
                  <strong>Rejection Reason:</strong> {{ log.meta?.['rejectionReason'] }}
                </div>
              }
              @if (log.meta?.['onHoldTill']) {
                <div class="meta-alert alert-hold mt-2">
                  <strong>On Hold Until:</strong> {{ log.meta?.['onHoldTill'] | date:'d MMM y' }}
                </div>
              }
              @if (log.meta?.['comment']) {
                <div class="meta-note mt-2">
                  <span class="note-label">Comment:</span> {{ log.meta?.['comment'] }}
                </div>
              }

              <!-- 5. Generic Key-Value fallback for any other keys -->
              @if (hasExtraMeta(log)) {
                <div class="extra-meta-toggle mt-2">
                  <button class="btn btn-link btn-sm p-0 meta-toggle-btn" (click)="toggleExpand(log._id)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" [class.rotated]="isExpanded(log._id)">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                    {{ isExpanded(log._id) ? 'Hide details' : 'More details' }}
                  </button>
                  @if (isExpanded(log._id)) {
                    <div class="generic-meta-grid mt-2">
                      @for (entry of assetLogsService.getGenericMetaEntries(log.meta, log.action); track entry.key) {
                        <div class="meta-pair">
                          <span class="meta-key">{{ entry.key }}:</span>
                          <span class="meta-value">{{ formatValue(entry.value) }}</span>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>

    <!-- Pagination -->
    @if (totalPages > 1) {
      <div class="history-pagination">
        <span class="page-info">
          Page <strong>&nbsp;{{ page }}&nbsp;</strong> of <strong>&nbsp;{{ totalPages }}&nbsp;</strong>
          <span class="page-total ml-1">({{ totalCounts }} records)</span>
        </span>
        <div class="pagination-controls">
          <button
            type="button"
            class="pagination-btn prev-btn"
            [disabled]="page <= 1"
            (click)="onPageChange(page - 1)"
            title="Previous page">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            Prev
          </button>
          <button
            type="button"
            class="pagination-btn next-btn"
            [disabled]="page >= totalPages"
            (click)="onPageChange(page + 1)"
            title="Next page">
            Next
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ml-1">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </div>
    }
  }

  <!-- Lightbox Modal for Image Preview -->
  @if (selectedPreviewImage) {
    <div class="lightbox-overlay" (click)="closeImagePreview()">
      <div class="lightbox-content" (click)="$event.stopPropagation()">
        <button class="lightbox-close-btn" (click)="closeImagePreview()" title="Close">
          &times;
        </button>
        <img [src]="selectedPreviewImage" alt="Preview Image" class="lightbox-img" />
      </div>
    </div>
  }
</div>
